import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { Box, Stack, Typography } from '@mui/material';
import { countPillStyles, typeIconStyles, typeRowStyles } from '@pages/CustomPoiTypes.styles';
import { resolveLabel, type PoiType } from '@store/api/poiTypeEndpoints';
import { AngleRightIcon, MapMarkerIcon } from 'react-line-awesome';

interface Props {
  poiType: PoiType;
  onOpen: () => void;
}

/**
 * One line of the catalogue. Deliberately just an identity and a size — everything that
 * can be edited lives on the type's own page, the way a team row opens a team.
 */
const PoiTypeRow: React.FC<Props> = ({ poiType, onOpen }) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  const fieldCount = poiType.fields.length
    + poiType.groups.reduce((total, group) => total + group.fields.length, 0);

  return (
    <Stack direction="row" spacing={2} sx={typeRowStyles} onClick={onOpen}>
      <Box sx={typeIconStyles}>
        <MapMarkerIcon />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }} noWrap>{resolveLabel(poiType.name, locales)}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap>{poiType.description}</Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
        <Box component="span" sx={countPillStyles}>
          {t('Customization_TypeSummary', [poiType.groups.length, fieldCount])}
        </Box>
        <Box component="span" sx={countPillStyles}>v{poiType.version}</Box>
        <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 18 }}>
          <AngleRightIcon />
        </Box>
      </Stack>
    </Stack>
  );
};

export default PoiTypeRow;
